import { Injectable, computed, inject, signal } from '@angular/core';
import { UserType } from '@enrollhere/classes';
import { injectQuery } from '@tanstack/angular-query-experimental';
import {
  FilterConfig,
  getRawValues,
  UnknownFilterDefinition,
} from 'src/app/common/utils/filters/filter-definition';
import { DateRangeValue } from 'src/app/common/components/filters/signals/date-dropdown-filter/date-dropdown-filter.component';
import { QueryState } from 'src/app/common/components/filters/signals/searchable-select-filter/searchable-select-filter.component';
import {
  PayoutApiService,
  PayoutFacetBucket,
} from '../services/payout-api.service';
import { SearchService, UserSearchResult } from '../services/search.service';

export interface PayoutFilterItem {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

export interface AgentItem {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  npn?: string;
}

export interface AccountItem {
  id: string;
  name?: string;
}

@Injectable({ providedIn: 'root' })
export class PayoutFilter implements UnknownFilterDefinition {
  private readonly payoutApiService = inject(PayoutApiService);
  private readonly searchService = inject(SearchService);

  userType = signal<UserType | undefined>(undefined);

  // Agent search state
  agentSearchTerm = signal('');
  // Loads the default agent list on first open instead of on page load
  agentDropdownOpened = signal(false);

  agentsQuery = injectQuery(() => ({
    queryKey: ['user-search', this.agentSearchTerm()],
    queryFn: () =>
      this.searchService.searchUsers({
        query: this.agentSearchTerm(),
        size: 100,
      }),
    enabled: this.agentDropdownOpened() || this.agentSearchTerm().length > 0,
  }));

  agentsQueryState = computed(
    (): QueryState<AgentItem> => ({
      data: (this.agentsQuery.data() ?? []).map(
        (user: UserSearchResult): AgentItem => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          name: user.name,
          npn: user.npn,
        })
      ),
      isLoading: this.agentsQuery.isLoading(),
      error: this.agentsQuery.error() ?? undefined,
    })
  );

  /** Consumed by the FilterContainerComponent */
  configs = computed(() => {
    const configs: FilterConfig<keyof PayoutFilter['values']>[] = [
      {
        name: 'paymentDate',
        title: 'Payment Date',
        location: 'default',
        filterComponent: () =>
          import('src/app/common/components/filters/signals/date-dropdown-filter/date-dropdown-filter.component').then(
            m => m.DateDropdownFilterComponent
          ),
        allowFuture: false,
      },
      {
        name: 'payoutTypes',
        title: 'Payout Type',
        location: 'additional',
        filterComponent: () =>
          import('src/app/common/components/filters/signals/searchable-select-filter/searchable-select-filter.component').then(
            m => m.SearchableSelectFilterComponent
          ),
        multiple: true,
        placeholder: 'Search payout types...',
        keyField: 'id',
        displayFn: (type: PayoutFilterItem) => type.name || type.id,
      },
    ];

    if (this.userType() !== UserType.AGENT) {
      configs.push({
        name: 'agentIds',
        title: 'Agent',
        location: 'additional',
        filterComponent: () =>
          import('src/app/common/components/filters/signals/searchable-select-filter/searchable-select-filter.component').then(
            m => m.SearchableSelectFilterComponent
          ),
        queryState: this.agentsQueryState,
        onSearchTermChange: (term: string) => this.agentSearchTerm.set(term),
        onDropdownOpenChange: (open: boolean) => {
          if (open) {
            this.agentDropdownOpened.set(true);
          }
        },
        multiple: true,
        placeholder: 'Search agents by name or NPN...',
        keyField: 'id',
        displayFn: (agent: AgentItem) => {
          if (agent.firstName && agent.lastName) {
            return `${agent.firstName} ${agent.lastName}`;
          }
          return agent.name || 'Unknown Agent';
        },
        sortFn: () => 0,
      });
    }

    configs.push({
      name: 'transactionAccountIds',
      title: 'Account',
      location: 'additional',
      filterComponent: () =>
        import('src/app/common/components/filters/signals/searchable-select-filter/searchable-select-filter.component').then(
          m => m.SearchableSelectFilterComponent
        ),
      multiple: true,
      placeholder: 'Filter by account...',
      keyField: 'id',
      displayFn: (account: AccountItem) => account.name || account.id,
      sortFn: () => 0,
      disabled: true,
      disabledMessage: 'Account filter is set by the page context',
      disableRemoveFilterButton: true,
    });

    return configs;
  });

  values = {
    paymentDate: signal<DateRangeValue | undefined>(undefined),
    payoutTypes: signal<PayoutFilterItem[]>([]),
    agentIds: signal<AgentItem[]>([]),
    transactionAccountIds: signal<AccountItem[]>([]),
    ownerIds: signal<string[]>([]), // Hidden filter - no UI, for parent-provided owner IDs
  };

  /** Flag to enable query without requiring specific filters */
  initialized = signal(false);

  resultsQueryKey = computed(() => [
    'payoutFilterResults',
    JSON.stringify(getRawValues(this.values)),
  ]);

  resultsEnabled = computed(
    () =>
      this.initialized() ||
      this.values.agentIds().length > 0 ||
      this.values.transactionAccountIds().length > 0 ||
      this.values.ownerIds().length > 0
  );

  async getResults(pageToken?: string) {
    // Helper to convert date range to timestamps
    const dateRangeToTimestamps = (range?: DateRangeValue) => {
      if (!range?.start && !range?.end)
        return { start: undefined, end: undefined };
      return {
        start: range?.start ? new Date(range.start).getTime() : undefined,
        end: range?.end ? new Date(range.end).getTime() : undefined,
      };
    };

    const paymentDateRange = dateRangeToTimestamps(this.values.paymentDate());

    // Expand combined "Add Funds" option to individual payout types
    const payoutTypes = this.values.payoutTypes().flatMap(x => {
      if (x.id === PayoutFilter.ADD_FUNDS_ID) {
        return this.addFundsTypes; // Expand to PT_TOP_UP and PT_AUTO_TOP_UP
      }
      return x.id;
    });

    const searchParams = {
      userIds: [
        ...this.values.agentIds().map(x => x.id),
        ...this.values.ownerIds(), // Hidden owner IDs from parent
      ],
      accountIds: this.values.transactionAccountIds().map(x => x.id),
      payoutTypes,
      paymentDateStart: paymentDateRange.start,
      paymentDateEnd: paymentDateRange.end,
      limit: 20,
      offset: pageToken ? Number(pageToken) : 0,
    };

    const result = await this.payoutApiService.searchPayouts(searchParams);

    return {
      nextPageToken:
        result.offset + result.payouts.length < result.total
          ? String(result.offset + result.payouts.length)
          : undefined,
      total: result.total,
      results: result.payouts,
    };
  }

  // Static query key since options are fetched without filters
  optionsQueryKey = computed(() => ['payoutFilterOptions']);

  optionsEnabled = computed(
    () =>
      this.initialized() ||
      this.values.agentIds().length > 0 ||
      this.values.transactionAccountIds().length > 0 ||
      this.values.ownerIds().length > 0
  );

  // Combined "Add Funds" option that represents both PT_TOP_UP and PT_AUTO_TOP_UP
  static readonly ADD_FUNDS_ID = 'ADD_FUNDS';
  private readonly addFundsTypes = ['PT_TOP_UP', 'PT_AUTO_TOP_UP'];

  async getOptions() {
    // Fetch all available options without applying filters
    // This ensures dropdown options remain stable regardless of current filter selections
    // Note: agentIds uses dynamic user search, and transactionAccountIds is set by parent component
    const payload = {
      requested: ['payoutTypes'] as string[],
    };

    const res = await this.payoutApiService.getAggregations(payload);
    const options: Partial<{
      payoutTypes: PayoutFilterItem[];
    }> = {};

    // Process payout types, combining PT_TOP_UP and PT_AUTO_TOP_UP into "Add Funds"
    const rawTypes = res.payoutTypes || [];
    const hasTopUp = rawTypes.some(t => t.id === 'PT_TOP_UP');
    const hasAutoTopUp = rawTypes.some(t => t.id === 'PT_AUTO_TOP_UP');

    options.payoutTypes = rawTypes
      .filter(item => !this.addFundsTypes.includes(item.id)) // Remove individual top-up types
      .map((item: PayoutFacetBucket) => ({
        id: item.id,
        name: item.name || item.id,
      }));

    // Add combined "Add Funds" option if either top-up type exists
    if (hasTopUp || hasAutoTopUp) {
      options.payoutTypes.push({
        id: PayoutFilter.ADD_FUNDS_ID,
        name: 'Add Funds',
      });
    }

    return options;
  }
}
